import { FC, forwardRef, ForwardedRef, useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import type { Annotation, PartialAnnotationStyle, StyleDataById } from './models';
import Container from './style/container';
import Image from './style/image';
import InnerContainer from './style/inner-container';
import useAnnotationEngine, { Handles, OnEvent } from './use-annotation-engine';

const CrossFadeImage: FC<{ src: string; onImageLoaded?: () => void }> = ({ src, onImageLoaded }) => {
    const [bottom, setBottom] = useState(src);
    const [top, setTop] = useState<string | null>(null);
    const [topVisible, setTopVisible] = useState(false);
    const topImgRef = useRef<HTMLImageElement>(null);

    const visible = topVisible ? top : bottom;
    const hidden = topVisible ? bottom : top;

    if (src !== visible && src !== hidden) {
        setBottom(visible);
        setTop(src);
        setTopVisible(false);
    }

    // If `src` matches the currently-hidden (already loaded) layer - e.g. navigating back
    // to the previous image - fade back to it instead of reloading. This is deferred to a
    // real animation frame (like revealTop below) rather than flipped during render, so the
    // browser actually paints the current opacity first and the CSS transition has
    // something to animate from; flipping it synchronously in render collapses both states
    // into a single commit and the fade never shows.
    useEffect(() => {
        if (src !== hidden) return undefined;
        const frame = requestAnimationFrame(() => {
            setTopVisible((v) => !v);
            onImageLoaded?.();
        });

        return () => cancelAnimationFrame(frame);
    }, [src, hidden, onImageLoaded]);

    const revealTop = useCallback(() => {
        requestAnimationFrame(() => {
            setTopVisible(true);
            onImageLoaded?.();
        });
    }, [onImageLoaded]);

    // If the browser already had `top` cached, `load` can fire before this effect attaches
    // its listener, leaving the image stuck invisible. Catch that case explicitly here.
    useEffect(() => {
        if (topImgRef.current?.complete && topImgRef.current.naturalWidth > 0) {
            revealTop();
        }
    }, [top, revealTop]);

    return (
        <>
            <Image src={bottom} style={{ opacity: 1 }} onLoad={!top ? () => onImageLoaded?.() : undefined} />
            {top && <Image key={top} ref={topImgRef} src={top} style={{ opacity: topVisible ? 1 : 0 }} onLoad={revealTop} />}
        </>
    );
};

export interface AnnotationEngineProps {
    className?: string;
    annotations: Annotation[];
    annotationToEdit?: Annotation;
    backgroundImagePath: string;
    foregroundImagePath?: string;
    id?: string;
    onEvent: OnEvent;
    styleForAnnotations?: StyleDataById;
    styleForAnnotationToEdit?: PartialAnnotationStyle;
    styleForPointsToEdit?: StyleDataById;
    children?: ReactNode;
    onBackgroundImageLoaded?: () => void;
}

export const AnnotationEngine: FC<AnnotationEngineProps> = forwardRef(
    (
        {
            annotationToEdit,
            annotations = [],
            backgroundImagePath,
            className,
            foregroundImagePath,
            id = 'annotation-engine',
            onEvent,
            styleForAnnotations,
            styleForAnnotationToEdit,
            styleForPointsToEdit,
            children,
            onBackgroundImageLoaded,
        },
        ref: ForwardedRef<Handles>,
    ) => {
        // calling conditionally a hook is not allowed, so this one will be used if {ref} is null
        const cRef = useRef<HTMLCanvasElement>(null);

        useAnnotationEngine({
            annotationToEdit,
            annotations,
            onEvent,
            canvasRef: cRef,
            ref,
            styleForAnnotations: styleForAnnotations ?? new Map(),
            styleForAnnotationToEdit,
            styleForPointsToEdit,
        });

        return (
            <Container className={className}>
                {children && <InnerContainer>{children}</InnerContainer>}
                <CrossFadeImage src={backgroundImagePath} onImageLoaded={onBackgroundImageLoaded} />
                {foregroundImagePath && <Image src={foregroundImagePath} />}
                <canvas ref={cRef} id={id} />
            </Container>
        );
    },
);

AnnotationEngine.displayName = 'AnnotationEngine';
